import { useMutation } from '@apollo/client';
import useMessages from 'hooks/useMessages';
import { CREATE_SITE, DELETE_SITE, UPDATE_SITE } from 'graphql/secure/mutations/site.mutations';
import {
  ICreateSiteInput,
  IUpdateSiteInput,
} from 'graphql/secure/mutations/types/site-mutation.types';
import { TLanguage } from 'DesignSystem/types/language.types';
import { ISite } from 'FeatureModules/Site/types/site.types';

const useSiteMutations = () => {
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const [createSite, { loading: loadingCreate }] = useMutation<
    { createSite: { site: ISite } },
    { input: ICreateSiteInput; lang?: TLanguage }
  >(CREATE_SITE, {
    onCompleted: site => {
      setSuccessMessage('Site successfully created');
    },
    onError: error => {
      if (error.graphQLErrors?.some(({ message }) => message === 'SITE_DUPLICATED_EXTERNAL_ID')) {
        setErrorMessage(
          'The site id already exists in the system. The id has to be unique across the platform.'
        );
      } else if (
        error.graphQLErrors?.some(({ message }) => message === 'MAPBOX_ERROR_MISSING_TOKEN')
      ) {
        setErrorMessage(
          'No Mapbox API token configured. Please add your Mapbox API token in Integration Settings.'
        );
      } else {
        setErrorMessage('There was an error creating your site');
      }
    },
    refetchQueries: ['GetSites'],
  });

  const [updateSite, { loading: loadingUpdate }] = useMutation<
    { updateSite: { site: ISite } },
    { id: string; input: IUpdateSiteInput; lang?: TLanguage }
  >(UPDATE_SITE, {
    onCompleted: () => {
      setSuccessMessage('Site successfully updated');
    },
    onError: error => {
      if (error.graphQLErrors?.some(({ message }) => message === 'SITE_DUPLICATED_EXTERNAL_ID')) {
        setErrorMessage(
          'The site id already exists in the system. The id has to be unique across the platform.'
        );
      } else if (
        error.graphQLErrors?.some(({ message }) => message === 'MAPBOX_ERROR_MISSING_TOKEN')
      ) {
        setErrorMessage(
          'No Mapbox API token configured. Please add your Mapbox API token in Integration Settings.'
        );
      } else {
        setErrorMessage('There was an error updating your site');
      }
    },
    refetchQueries: ['GetSites', 'GetDataset', 'GetGeoDataProcess'],
  });

  const [deleteSite, { loading: loadingDelete }] = useMutation<
    { deleteSite: { id: string; siteClusterSiteIds?: string[] } },
    { id: string; deleteSitesFromCluster?: boolean }
  >(DELETE_SITE, {
    onCompleted: () => {
      setSuccessMessage('Site successfully deleted');
    },
    onError: () => setErrorMessage('There was an error deleting your site'),
    refetchQueries: ['GetSites', 'GetDataset', 'GetGeoDataProcess', 'GetPlots'],
  });

  return {
    loading: loadingCreate || loadingUpdate || loadingDelete,
    createSite,
    updateSite,
    deleteSite,
  };
};

export default useSiteMutations;
